/**
 * The single source of the logged-in identity used to NAME per-user storage
 * (the `Backups_<firmId>_<userId>` / `Autosave_<firmId>_<userId>` localForage
 * stores and the `Queues_<firmId>_<userId>` Dexie database).
 *
 * Identity lives in sessionStorage, which is per browser tab. localStorage is
 * shared by every tab on the origin, so a second tab logging into a different
 * firm overwrites it for the first tab as well. Until OWA-785 the generated
 * reducers named the backup store from localStorage while restore and the
 * queue database named it from sessionStorage: with two firms open, the older
 * tab wrote its firm's entities into the other firm's backup store, and the
 * other firm restored them into its UI.
 *
 * Everything that names a store now reads it from here. Deliberately free of
 * imports so it is safe inside the storage web worker and under node, where
 * sessionStorage does not exist.
 */

/**
 * sessionStorage keys naming the account bound to this tab. A locked tab keeps
 * them so retained work can still reach that account's local stores. Network
 * and UI authority comes from TabSession readiness, never from these keys.
 */
export const FIRM_ID_KEY = 'firmId';
export const USER_ID_KEY = 'userId';

/**
 * Read one identity key, tolerating environments with no sessionStorage
 * (web workers, node) and browsers that throw on access in private mode.
 *
 * @param {string} key
 * @returns {string} the value, or '' when it cannot be read
 */
function read(key: string): string {
    if (typeof sessionStorage === 'undefined') return '';

    try {
        return sessionStorage.getItem(key) ?? '';
    }
    catch {
        // Safari private mode and some embedded webviews throw on access.
        return '';
    }
}

/**
 * The firm whose local data stores belong to this tab.
 *
 * @returns {string} the firmId, or '' when this tab has no bound account
 */
export function getSessionFirmId(): string {
    return read(FIRM_ID_KEY);
}

/**
 * The user whose local data stores belong to this tab.
 *
 * @returns {string} the userId, or '' when this tab has no bound account
 */
export function getSessionUserId(): string {
    return read(USER_ID_KEY);
}

/**
 * Is a complete identity available for naming a store?
 *
 * Callers that would otherwise name a store from a partial identity must not
 * write at all: a `Backups__` or `Backups_<firm>_` store is not this user's
 * store, and anything written there is both lost and misfiled.
 *
 * @returns {boolean}
 */
export function hasSessionIdentity(): boolean {
    return !!getSessionFirmId() && !!getSessionUserId();
}
